import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FLIPBOOK_URL } from "../lib/links";
import { audio } from "../lib/audio";

const MEMORY_SOURCES = ["book", "hall-flipbook-link", "sign"];

// The book opening: its cover and first page turn while the view leans into it, then the flipbook itself
function BookOpening() {
  const page = "absolute top-0 h-full w-1/2 rounded-r-md bg-[#FBF4E6] shadow-[0_10px_30px_-12px_rgba(90,60,30,0.5)]";
  return (
    <motion.div
      data-testid="flipbook-opening"
      initial={{ scale: 0.55, opacity: 0 }}
      animate={{ scale: [0.55, 1, 2.6], opacity: [0, 1, 0] }}
      transition={{ duration: 1.25, times: [0, 0.45, 1], ease: "easeInOut" }}
      className="pointer-events-none absolute left-1/2 top-1/2 h-[46vmin] w-[68vmin] -translate-x-1/2 -translate-y-1/2 [perspective:1200px]"
    >
      <div className="absolute inset-0 rounded-md bg-[#6E2F3C]" />
      <div className={`${page} left-0 rounded-l-md rounded-r-none`} />
      <div className={`${page} left-1/2`} />
      {[0, 0.12, 0.24].map((d) => (
        <motion.div
          key={d}
          initial={{ rotateY: 0 }}
          animate={{ rotateY: -180 }}
          transition={{ duration: 0.55, delay: 0.1 + d, ease: "easeInOut" }}
          style={{ transformOrigin: "left center" }}
          className={`${page} left-1/2 border-l border-[#E3D3B5] [backface-visibility:visible]`}
        />
      ))}
    </motion.div>
  );
}

export function FlipbookOverlay({ onChange, atMemory }) {
  const [from, setFrom] = useState(null);
  const [back, setBack] = useState("the gallery");
  const [shown, setShown] = useState(false);
  const open = !!from;
  useEffect(() => {
    const on = (e) => {
      const src = e.detail || "link";
      setBack(MEMORY_SOURCES.includes(src) || atMemory ? "the Memory Wall" : "the gallery");
      setFrom(src);
    };
    window.addEventListener("afae-flipbook", on);
    return () => window.removeEventListener("afae-flipbook", on);
  }, [atMemory]);
  useEffect(() => {
    onChange(open);
    audio.duck?.(open);
    if (!open) setShown(false);
    window.__afaeFlipbook = { open, from };
  }, [open, from, onChange]);
  useEffect(() => {
    if (!open) return;
    const close = () => setFrom(null);
    const key = (e) => e.key === "Escape" && close();
    const msg = (e) => e.origin === window.location.origin && e.data?.afae === "flipbook-close" && close();
    const t = setTimeout(() => setShown(true), 950);
    window.addEventListener("keydown", key);
    window.addEventListener("message", msg);
    return () => {
      clearTimeout(t);
      window.removeEventListener("keydown", key);
      window.removeEventListener("message", msg);
    };
  }, [open]);
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="flipbook"
          data-testid="flipbook-overlay"
          data-from={from}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.45 }}
          className="fixed inset-0 z-[95] overflow-hidden bg-[#F6EEDF]"
        >
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_45%,rgba(255,201,138,0.55),transparent_65%)]" />
          {!shown && <BookOpening />}
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: shown ? 1 : 0, scale: shown ? 1 : 0.97 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-x-0 bottom-0 top-[calc(env(safe-area-inset-top)+3.25rem)] overflow-auto pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)] [-webkit-overflow-scrolling:touch]"
          >
            <iframe data-testid="flipbook-frame" title="The Flipbook" src={`${FLIPBOOK_URL}?embed=1`} className="block h-full w-full border-0 bg-transparent" />
          </motion.div>
          <div className="absolute inset-x-0 top-0 flex h-[calc(env(safe-area-inset-top)+3.25rem)] items-end px-[max(1rem,env(safe-area-inset-left))] pb-2">
            <button
              data-testid="flipbook-back-button"
              onClick={() => setFrom(null)}
              className="rounded-full border border-[#E2B66B]/70 bg-[#FFF9EE]/95 px-4 py-1.5 font-display text-lg italic text-[#4A2D55] shadow-[0_10px_30px_-18px_rgba(160,110,40,0.8)] transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-white"
            >
              ← back to {back}
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
